import { useTranslation } from "react-i18next"

import { baseColumnDef } from "@/nzds/data-grid"
import type { BillGridRecord } from "@/services/bill/bill.types"
import { useCostColumn } from "@/utils/grid-columns/use-cost-column"
import { useFlowDirectionColumn } from "@/utils/grid-columns/use-flow-direction-column"
import { useMeterCodeColumn } from "@/utils/grid-columns/use-meter-code-column"
import { useSiteNameColumn } from "@/utils/grid-columns/use-site-name-column"
import { useUsageColumn } from "@/utils/grid-columns/use-usage-column"

import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid-pro"

type CommonBillColumnName =
  | "cost"
  | "flowDirection"
  | "meterCode"
  | "periodEnd"
  | "periodStart"
  | "siteName"
  | "usage"

type CommonBillFieldName = CommonBillColumnName | "currency" | "usageUnit"

const DEFAULT_FIELDS: Record<CommonBillFieldName, string> = {
  cost: "cost",
  currency: "currency",
  flowDirection: "flow_direction",
  meterCode: "meter_code",
  periodEnd: "end_date",
  periodStart: "start_date",
  siteName: "site_name",
  usage: "usage",
  usageUnit: "usage_unit",
}

interface UseCommonBillColumnsOptions {
  /** Row field names, for grids whose rows are not snake_cased bill records */
  fields?: Partial<Record<CommonBillFieldName, string>>
}

export const useCommonBillColumns = <
  TRow extends GridValidRowModel = BillGridRecord,
>(
  options: UseCommonBillColumnsOptions = {}
): Record<CommonBillColumnName, GridColDef<TRow>> => {
  const { t } = useTranslation("common")
  const fields = { ...DEFAULT_FIELDS, ...options.fields }
  const costColumn = useCostColumn<TRow>({
    currencyField: fields.currency,
    field: fields.cost,
  })
  const flowDirectionColumn = useFlowDirectionColumn<TRow>({
    field: fields.flowDirection,
  })
  const meterCodeColumn = useMeterCodeColumn<TRow>({ field: fields.meterCode })
  const siteNameColumn = useSiteNameColumn<TRow>({ field: fields.siteName })
  const usageColumn = useUsageColumn<TRow>({
    convert: false,
    field: fields.usage,
    precision: 2,
    unitField: fields.usageUnit,
  })

  return {
    cost: costColumn,
    flowDirection: flowDirectionColumn,
    meterCode: meterCodeColumn,
    periodEnd: {
      ...baseColumnDef,
      field: fields.periodEnd,
      filterable: false,
      flex: 1,
      headerName: t("bill.periodEnd"),
      maxWidth: 180,
      minWidth: 120,
      sortable: true,
    },
    periodStart: {
      ...baseColumnDef,
      field: fields.periodStart,
      filterable: false,
      flex: 1,
      headerName: t("bill.periodStart"),
      maxWidth: 180,
      minWidth: 120,
      sortable: true,
    },
    siteName: siteNameColumn,
    usage: usageColumn,
  }
}
